import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

import THEME from '../../constants/theme';
import { useIntersectionObserver } from '../../hooks';

/**
 * ComparisonTable component for displaying strategy comparison data
 *
 * @param {Object} props - Component props
 * @param {Array} props.headers - Array of header labels (first is feature name, rest are column names)
 * @param {Array} props.rows - Array of row objects with { feature, values: [] }
 * @param {Object} props.sx - Additional MUI styling
 * @returns {React.Component} ComparisonTable component
 */
const ComparisonTable = ({ headers = [], rows = [], sx = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef = React.useRef(null);
  const isVisible = useIntersectionObserver(tableRef);

  return (
    <TableContainer
      ref={tableRef}
      component={Paper}
      sx={{
        boxShadow: THEME.SHADOWS.LARGE,
        overflow: isMobile ? 'auto' : 'hidden',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity ${THEME.TRANSITIONS.SLOW} ease, transform ${THEME.TRANSITIONS.SLOW} ease`,
        ...sx
      }}
    >
      <Table
        sx={{
          minWidth: isMobile ? 500 : 'auto'
        }}
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell
                key={index}
                sx={{
                  backgroundColor:
                    index === 0
                      ? THEME.COLORS.NEUTRAL_LIGHT_GRAY
                      : THEME.COLORS.NEUTRAL_LIGHT_GRAY,
                  color: THEME.COLORS.NEUTRAL_BLACK,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  padding: THEME.SPACING.LARGE,
                  borderRight:
                    index < headers.length - 1
                      ? `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
                      : 'none'
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              component={motion.tr}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: isVisible ? 1 : 0,
                x: isVisible ? 0 : -20
              }}
              transition={{ duration: 0.5, delay: rowIndex * 0.1 }}
              sx={{
                '&:hover': {
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT,
                  cursor: 'pointer'
                },
                '&:last-child td': {
                  borderBottom: 0
                }
              }}
            >
              {/* Feature Column (First Column - Light Gray) */}
              <TableCell
                sx={{
                  backgroundColor: THEME.COLORS.NEUTRAL_LIGHT_GRAY,
                  color: THEME.COLORS.NEUTRAL_BLACK,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  padding: THEME.SPACING.LARGE,
                  borderRight: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
                }}
              >
                {row.feature}
              </TableCell>

              {/* Value Columns (Gray Text) */}
              {row.values.map((value, valueIndex) => (
                <TableCell
                  key={valueIndex}
                  sx={{
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                    padding: THEME.SPACING.LARGE,
                    borderRight:
                      valueIndex < row.values.length - 1
                        ? `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
                        : 'none'
                  }}
                >
                  {value}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ComparisonTable;
